/**
 * VendorRatingShopService
 * Service for public shop endpoints in VendorRatingManagement
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.VendorRatingManagement)
 *
 * Read endpoints are anonymous. Write endpoints require Customer, Staff, or Volunteer role.
 */
import { BaseService } from '../base/BaseService';
/**
 * VendorRatingShopService
 * Public endpoints for vendor ratings and flags.
 * Read endpoints are anonymous. Write endpoints require authentication.
 */
export class VendorRatingShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorRatingShopService.DEFAULT_SERVICE_PATH,
            basePath: VendorRatingShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ ENDPOINTS (Anonymous) ====================
    /**
     * Get all ratings for a vendor
     * GET /api/shop/ratings/vendor/{vendorId}
     */
    async getRatingsForVendor(vendorId) {
        const response = await this.getRatingsForVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get ratings for vendor (raw response)
     */
    getRatingsForVendorRaw(vendorId) {
        const endpoint = this.getEndpoint('ratings-for-vendor', `ratings/vendor/${vendorId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single rating by ID with vendor response
     * GET /api/shop/ratings/{id}
     */
    async getRatingById(id) {
        const response = await this.getRatingByIdRaw(id);
        if (!response.data) {
            throw new Error(`Rating ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get rating by ID (raw response)
     */
    getRatingByIdRaw(id) {
        const endpoint = this.getEndpoint('rating-by-id', `ratings/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get aggregated rating summary for a vendor
     * GET /api/shop/ratings/vendor/{vendorId}/summary
     */
    async getVendorRatingSummary(vendorId) {
        const response = await this.getVendorRatingSummaryRaw(vendorId);
        if (!response.data) {
            throw new Error(`Rating summary for vendor ${vendorId} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor rating summary (raw response)
     */
    getVendorRatingSummaryRaw(vendorId) {
        const endpoint = this.getEndpoint('vendor-rating-summary', `ratings/vendor/${vendorId}/summary`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE ENDPOINTS (Authenticated) ====================
    /**
     * Create a new vendor rating
     * POST /api/shop/ratings
     * Requires: Customer, Staff, or Volunteer role
     */
    async createRating(request) {
        const response = await this.createRatingRaw(request);
        if (!response.data) {
            throw new Error('Failed to create rating');
        }
        return response.data;
    }
    /**
     * Create rating (raw response)
     */
    createRatingRaw(request) {
        const endpoint = this.getEndpoint('create-rating', 'ratings');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Create a vendor flag
     * POST /api/shop/flags
     * Requires: Customer, Staff, or Volunteer role
     */
    async createFlag(request) {
        const response = await this.createFlagRaw(request);
        if (!response.data) {
            throw new Error('Failed to create flag');
        }
        return response.data;
    }
    /**
     * Create flag (raw response)
     */
    createFlagRaw(request) {
        const endpoint = this.getEndpoint('create-flag', 'flags');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
}
VendorRatingShopService.DEFAULT_SERVICE_PATH = 'vendor-ratings';
VendorRatingShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=VendorRatingShopService.js.map